// ============================================================================
// Accept a payment against an existing estimate — writes BOTH the local ledger
// row (EstimatePaymentTransaction) and the matching Zoho advance credit in the
// test org, which is what the prepaid ship gate reads.
//
// This is the manual stand-in for a Razorpay webhook, which the test box never
// receives. Real money is never involved; the Zoho org is the TEST org.
// ============================================================================
import {
  Alert,
  Button,
  Card,
  Descriptions,
  Divider,
  InputNumber,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from "antd";
import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  CollectPaymentResult,
  PaymentState,
  PaymentTransaction,
  collectPayment,
  getPaymentState,
} from "./api";

const { Text, Paragraph } = Typography;

const OmsTestPaymentPanel: React.FC = () => {
  const [estimateId, setEstimateId] = useState<number | undefined>();
  const [state, setState] = useState<PaymentState | undefined>();
  const [amount, setAmount] = useState<number | undefined>();
  const [looking, setLooking] = useState(false);
  const [paying, setPaying] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const [warning, setWarning] = useState<string | undefined>();
  const [last, setLast] = useState<CollectPaymentResult | undefined>();

  const readError = (e: any) => {
    setWarning(e?.response?.data?.warning);
    return String(
      e?.response?.data?.detail ?? e?.message ?? "Request failed"
    );
  };

  const onLookup = async () => {
    if (estimateId === undefined) return;
    setLooking(true);
    setError(undefined);
    setWarning(undefined);
    setLast(undefined);
    try {
      const data = await getPaymentState(estimateId);
      setState(data);
      // Default to whatever is still outstanding — the reason this screen exists.
      setAmount(Number(data.balance_rupees) || undefined);
    } catch (e: any) {
      setState(undefined);
      setError(readError(e));
    } finally {
      setLooking(false);
    }
  };

  const onPay = async () => {
    if (estimateId === undefined) return;
    setPaying(true);
    setError(undefined);
    setWarning(undefined);
    try {
      const data = await collectPayment({
        estimate_id: estimateId,
        amount_rupees: amount,
      });
      setLast(data);
      setState(data.state);
      setAmount(Number(data.state.balance_rupees) || undefined);
      message.success(`Recorded ₹${data.payment.amount_rupees}`);
    } catch (e: any) {
      setError(readError(e));
      // The ledger may have moved even on failure — re-read rather than show stale numbers.
      try {
        setState(await getPaymentState(estimateId));
      } catch {
        /* keep the error already on screen */
      }
    } finally {
      setPaying(false);
    }
  };

  const txnColumns = [
    { title: "Ledger id", dataIndex: "id", key: "id" },
    { title: "Reference", dataIndex: "payment_id", key: "payment_id" },
    {
      title: "Amount",
      dataIndex: "amount_rupees",
      key: "amount_rupees",
      render: (v: string) => `₹${v}`,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (s: string | null) => (
        <Tag color={s === "captured" || s === "authorized" ? "green" : "default"}>
          {s ?? "—"}
        </Tag>
      ),
    },
    { title: "Method", dataIndex: "method", key: "method" },
  ];

  return (
    <Card size="small" title="Accept a payment on an existing estimate" style={{ marginTop: 16 }}>
      <Paragraph type="secondary" style={{ fontSize: 12 }}>
        Writes the payment ledger row <em>and</em> a matching Zoho advance credit in the test
        org — the pair the prepaid ship gate looks for. Use it to release an order whose
        outbound leg is holding for money, since no Razorpay webhook reaches this box.
      </Paragraph>

      <Space wrap align="end" style={{ marginBottom: 12 }}>
        <div>
          <Text strong>Estimate id</Text>
          <InputNumber
            style={{ width: 160, display: "block", marginTop: 4 }}
            min={1}
            value={estimateId}
            onChange={(v) => setEstimateId(v === null ? undefined : Number(v))}
            onPressEnter={onLookup}
          />
        </div>
        <Button onClick={onLookup} loading={looking} disabled={estimateId === undefined}>
          Look up
        </Button>
      </Space>

      {error ? (
        <Alert
          type="error"
          showIcon
          message="Payment failed"
          description={
            <>
              <pre style={{ whiteSpace: "pre-wrap", margin: 0 }}>{error}</pre>
              {warning ? <div style={{ marginTop: 8 }}>{warning}</div> : null}
            </>
          }
          style={{ marginBottom: 12 }}
        />
      ) : null}

      {state ? (
        <>
          <Descriptions size="small" column={2} bordered>
            <Descriptions.Item label="Client">{state.client}</Descriptions.Item>
            <Descriptions.Item label="Sales order">
              {state.sales_order_id ? (
                <Link to={`/oms/${state.sales_order_id}`}>{state.sales_order_id}</Link>
              ) : (
                "— (not placed yet)"
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Payable">₹{state.payable_rupees}</Descriptions.Item>
            <Descriptions.Item label="Paid">₹{state.paid_rupees}</Descriptions.Item>
            <Descriptions.Item label="Balance">
              <Text strong type={Number(state.balance_rupees) > 0 ? "danger" : "success"}>
                ₹{state.balance_rupees}
              </Text>
            </Descriptions.Item>
            <Descriptions.Item label="Terms">{state.payment_terms ?? "—"}</Descriptions.Item>
            <Descriptions.Item label="Cleared to ship" span={2}>
              {state.cleared_to_ship === null ? (
                <Tag>no order yet</Tag>
              ) : state.cleared_to_ship ? (
                <Tag color="green">yes</Tag>
              ) : (
                <Tag color="orange">no — outbound leg will hold</Tag>
              )}
            </Descriptions.Item>
          </Descriptions>

          {!state.zoho_reachable ? (
            <Alert
              type="warning"
              showIcon
              style={{ marginTop: 12 }}
              message="Outbox is deferred here, so no Zoho advance will be created — only the local ledger row."
            />
          ) : null}

          <Space align="end" style={{ marginTop: 12 }} wrap>
            <div>
              <Text strong>Amount (₹)</Text>
              <InputNumber
                style={{ width: 200, display: "block", marginTop: 4 }}
                min={0.01}
                step={100}
                value={amount}
                onChange={(v) => setAmount(v === null ? undefined : Number(v))}
              />
            </div>
            <Button type="primary" onClick={onPay} loading={paying}>
              Record payment
            </Button>
          </Space>

          {last ? (
            <Alert
              type="success"
              showIcon
              style={{ marginTop: 12 }}
              message={`₹${last.payment.amount_rupees} recorded — reference ${last.payment.reference}`}
              description={
                last.payment.zoho_payment_id
                  ? `Zoho advance credit ${last.payment.zoho_payment_id}`
                  : "No Zoho credit was created (see the note above)."
              }
            />
          ) : null}

          <Divider orientation="left" style={{ marginTop: 16 }}>
            Payment ledger
          </Divider>
          <Table<PaymentTransaction>
            size="small"
            rowKey="id"
            pagination={false}
            dataSource={state.transactions}
            columns={txnColumns}
            locale={{ emptyText: "No payments recorded yet" }}
          />
        </>
      ) : null}
    </Card>
  );
};

export default OmsTestPaymentPanel;
